// client/src/components/AdminDashboard.jsx
import React, { useState, useEffect } from "react";
import QRCode from "qrcode";
import {
  Bell,
  Settings,
  Gauge,
  List,
  Users,
  Database,
  AlertCircle,
  LogOut,
} from "lucide-react";

const API_BASE = "https://hostel-laundry-api.onrender.com";

/* ============================================================
   DEMO DATA — used when localStorage.demoAdmin === "true"
   ============================================================ */
const DEMO_STATS = { total_orders: 47, ready: 5, washing: 8, drying: 4 };

const DEMO_ORDERS_INIT = [
  { id: 1042, usn: "4so22cs001", status: "washing", shirts: 3, pants: 2, uniforms: 1, towels: 0, bedsheets: 1, created_at: new Date(Date.now() - 3600000).toISOString() },
  { id: 1041, usn: "4so22cs015", status: "drying", shirts: 2, pants: 1, uniforms: 0, towels: 1, bedsheets: 0, created_at: new Date(Date.now() - 7200000).toISOString() },
  { id: 1040, usn: "4so22cs032", status: "ready", shirts: 4, pants: 3, uniforms: 2, towels: 0, bedsheets: 2, created_at: new Date(Date.now() - 86400000).toISOString() },
  { id: 1039, usn: "4so22cs008", status: "delivered", shirts: 1, pants: 1, uniforms: 1, towels: 0, bedsheets: 0, created_at: new Date(Date.now() - 86400000 * 2).toISOString() },
  { id: 1038, usn: "4so22cs021", status: "delivered", shirts: 5, pants: 2, uniforms: 0, towels: 1, bedsheets: 1, created_at: new Date(Date.now() - 86400000 * 3).toISOString() },
];

const DEMO_STUDENTS_INIT = [
  { id: 1, usn: "4so22cs001", name: "Rahul Sharma", hostel: "boys", room: "A-101", phone: "+91 98765 43210", email: "rahul@sjec.ac.in" },
  { id: 2, usn: "4so22cs015", name: "Priya Nair", hostel: "girls", room: "B-205", phone: "+91 98765 43211", email: "priya@sjec.ac.in" },
  { id: 3, usn: "4so22cs032", name: "Aditya Patel", hostel: "boys", room: "A-312", phone: "+91 98765 43212", email: "aditya@sjec.ac.in" },
  { id: 4, usn: "4so22cs008", name: "Sneha Kulkarni", hostel: "girls", room: "B-110", phone: "+91 98765 43213", email: "sneha@sjec.ac.in" },
  { id: 5, usn: "4so22cs021", name: "Mohammed Faizan", hostel: "boys", room: "C-402", phone: "+91 98765 43214", email: "faizan@sjec.ac.in" },
];

/* ============================================================
   NAV ITEMS
   ============================================================ */
const navItems = [
  { key: "dashboard", label: "Dashboard", icon: Gauge },
  { key: "orders", label: "Orders", icon: List },
  { key: "students", label: "Students", icon: Users },
];

const navItemsFull = [
  ...navItems,
  { key: "db", label: "DB Editor", icon: Database },
];

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const isDemo = localStorage.getItem("demoAdmin") === "true";

  const tabs = isDemo ? navItems : navItemsFull;

  const handleLogout = () => {
    localStorage.removeItem("demoAdmin");
    window.location.href = "/admin-login";
  };

  return (
    <div className="min-h-screen bg-white flex justify-center px-4 py-6">
      <div className="w-full max-w-5xl">
        <div className="bg-white rounded-2xl border border-neutral-200 p-6 md:p-8">

          {/* DEMO BANNER */}
          {isDemo && (
            <div className="flex items-center gap-2 px-4 py-2.5 mb-4 bg-neutral-100 border border-neutral-200 rounded-xl text-sm text-neutral-600">
              <AlertCircle className="w-4 h-4 text-neutral-500 flex-shrink-0" />
              <span>
                <strong className="text-neutral-900">Demo Mode</strong> — View orders, change statuses, manage students & view QR codes. All changes are local only.
              </span>
            </div>
          )}

          {/* HEADER */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-neutral-900 tracking-tight">
                Laundroid Admin
              </h1>
              <p className="text-neutral-500 text-sm mt-1">
                SJEC Hostel Laundry · {isDemo ? "Demo Panel" : "Admin Panel"}
              </p>
            </div>

            <div className="flex items-center gap-3">
              {!isDemo && (
                <>
                  <button className="relative p-2 rounded-full bg-neutral-100 hover:bg-neutral-200 transition">
                    <Bell className="w-4 h-4 text-neutral-700" />
                  </button>
                  <button className="p-2 rounded-full bg-neutral-100 hover:bg-neutral-200 transition">
                    <Settings className="w-4 h-4 text-neutral-700" />
                  </button>
                </>
              )}

              {isDemo && (
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-neutral-100 border border-neutral-200 text-sm font-medium text-neutral-700 hover:bg-neutral-200 transition"
                >
                  <LogOut className="w-4 h-4" />
                  Exit Demo
                </button>
              )}

              <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-neutral-50 border border-neutral-200">
                <div className="w-8 h-8 rounded-full bg-neutral-900 flex items-center justify-center text-white text-xs font-semibold">
                  {isDemo ? "DM" : "LS"}
                </div>
                <div>
                  <p className="text-sm font-semibold text-neutral-900">
                    {isDemo ? "Demo User" : "Laundry Staff"}
                  </p>
                  <p className="text-xs text-neutral-400">{isDemo ? "Demo · Full Access" : "Admin · On Duty"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* TABS */}
          <div className="mt-6 border-b border-neutral-100">
            <div className="flex gap-2 pb-2 overflow-x-auto">
              {tabs.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                    activeTab === key
                      ? "bg-neutral-900 text-white"
                      : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* CONTENT */}
          <div className="mt-6 space-y-6">
            {activeTab === "dashboard" && <DashboardView isDemo={isDemo} />}
            {activeTab === "orders" && <OrdersView isDemo={isDemo} />}
            {activeTab === "students" && <StudentsView isDemo={isDemo} />}
            {activeTab === "db" && !isDemo && <DBEditor />}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   DASHBOARD VIEW
   ============================================================ */
function DashboardView({ isDemo }) {
  const [stats, setStats] = useState(isDemo ? DEMO_STATS : null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isDemo) return;
    fetch(`${API_BASE}/api/admin/overview`)
      .then((res) => res.json())
      .then((data) => setStats(data))
      .catch(() => setError("Failed to load dashboard data"));
  }, []);

  if (error) return <p className="text-sm text-neutral-500">{error}</p>;
  if (!stats) return <p className="text-sm text-neutral-400">Loading...</p>;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatCard label="Total Orders" value={stats.total_orders} />
      <StatCard label="Ready" value={stats.ready} />
      <StatCard label="Washing" value={stats.washing} />
      <StatCard label="Drying" value={stats.drying} />
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-xl bg-neutral-50 border border-neutral-100 px-4 py-4">
      <p className="text-xs text-neutral-500 font-medium">{label}</p>
      <p className="text-2xl font-bold text-neutral-900 mt-1">{value ?? "-"}</p>
    </div>
  );
}

/* ============================================================
   ORDERS VIEW — demo users CAN change status (locally)
   ============================================================ */
function OrdersView({ isDemo }) {
  const [orders, setOrders] = useState(isDemo ? [...DEMO_ORDERS_INIT] : []);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isDemo) return;
    fetch(`${API_BASE}/api/admin/orders`)
      .then((res) => res.json())
      .then((data) => setOrders(Array.isArray(data) ? data : []))
      .catch(() => setError("Failed to load orders"));
  }, []);

  const updateStatus = (id, status) => {
    if (isDemo) {
      // Update locally in demo
      setOrders((prev) =>
        prev.map((o) => (o.id === id ? { ...o, status } : o))
      );
      return;
    }

    fetch(`${API_BASE}/orders/${id}/status`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        setOrders((prev) =>
          prev.map((o) => (o.id === id ? { ...o, status } : o))
        );
      })
      .catch(() => alert("Failed to update status"));
  };

  const statuses = ["received", "washing", "drying", "ready", "delivered"];

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-5">
      <h2 className="text-base font-semibold text-neutral-900 mb-3">Orders</h2>

      {error && <p className="text-sm text-neutral-500 mb-2">{error}</p>}

      {orders.length === 0 ? (
        <p className="text-sm text-neutral-400">No orders found.</p>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div
              key={o.id}
              className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 p-3 border border-neutral-200 rounded-xl hover:border-neutral-300 transition"
            >
              <div>
                <p className="font-semibold text-neutral-900 text-sm">
                  #{o.id} · {o.usn}
                </p>
                <p className="text-xs text-neutral-400">
                  {o.created_at?.slice(0, 19).replace("T", " ")}
                </p>
                <p className="text-xs text-neutral-500 mt-0.5">
                  S:{o.shirts} P:{o.pants} U:{o.uniforms} T:{o.towels} B:
                  {o.bedsheets}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <StatusPill status={o.status} />
                <select
                  className="border border-neutral-200 rounded-lg px-2 py-1 text-xs bg-white focus:ring-2 focus:ring-neutral-900 outline-none"
                  value={o.status}
                  onChange={(e) => updateStatus(o.id, e.target.value)}
                >
                  {statuses.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   STUDENTS VIEW — demo users can add, delete & see QR codes
   ============================================================ */
function StudentsView({ isDemo }) {
  const [students, setStudents] = useState([]);
  const [qrCodes, setQrCodes] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    usn: "",
    name: "",
    hostel: "boys",
    room: "",
    phone: "",
    email: "",
  });

  // Generate QR codes for demo students
  const generateQRCodes = async (studentList) => {
    const codes = {};
    for (const s of studentList) {
      try {
        codes[s.usn] = await QRCode.toDataURL(s.usn, { width: 120, margin: 1 });
      } catch (e) {
        codes[s.usn] = null;
      }
    }
    setQrCodes((prev) => ({ ...prev, ...codes }));
  };

  const loadStudents = () => {
    if (isDemo) {
      const demoList = [...DEMO_STUDENTS_INIT];
      setStudents(demoList);
      generateQRCodes(demoList);
      return;
    }
    setLoading(true);
    fetch(`${API_BASE}/api/admin/students`)
      .then((res) => res.json())
      .then((data) => setStudents(Array.isArray(data) ? data : []))
      .catch(() => setError("Failed to load students"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadStudents();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.usn || !form.name) {
      alert("USN and name are required");
      return;
    }

    if (isDemo) {
      // Add locally only
      const newStudent = {
        id: Date.now(),
        usn: form.usn,
        name: form.name,
        hostel: form.hostel,
        room: form.room,
        phone: form.phone,
        email: form.email,
      };
      setStudents((prev) => [newStudent, ...prev]);
      // Generate QR for new student
      try {
        const qr = await QRCode.toDataURL(form.usn, { width: 120, margin: 1 });
        setQrCodes((prev) => ({ ...prev, [form.usn]: qr }));
      } catch (e) { /* ignore */ }
      setForm({ usn: "", name: "", hostel: "boys", room: "", phone: "", email: "" });
      return;
    }

    fetch(`${API_BASE}/api/admin/students`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        setForm({
          usn: "",
          name: "",
          hostel: "boys",
          room: "",
          phone: "",
          email: "",
        });
        loadStudents();
      })
      .catch(() => alert("Failed to add student"));
  };

  const handleDelete = (usn) => {
    if (!window.confirm(`Delete student ${usn}?`)) return;

    if (isDemo) {
      setStudents((prev) => prev.filter((s) => s.usn !== usn));
      return;
    }

    fetch(`${API_BASE}/api/admin/students/${usn}`, {
      method: "DELETE",
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        setStudents((prev) => prev.filter((s) => s.usn !== usn));
      })
      .catch(() => alert("Failed to delete student"));
  };

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-5 space-y-5">
      <h2 className="text-base font-semibold text-neutral-900">Students</h2>

      {/* ADD FORM */}
      <form
        onSubmit={handleAdd}
        className="p-4 rounded-xl bg-neutral-50 border border-neutral-100 space-y-3"
      >
        <p className="font-medium text-sm text-neutral-900">Add New Student {isDemo && <span className="text-neutral-400 font-normal">(local only in demo)</span>}</p>
        <div className="grid md:grid-cols-3 gap-2">
          <input
            className="border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-neutral-900 outline-none"
            placeholder="USN"
            value={form.usn}
            onChange={(e) => setForm({ ...form, usn: e.target.value })}
          />
          <input
            className="border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-neutral-900 outline-none"
            placeholder="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <select
            className="border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-neutral-900 outline-none"
            value={form.hostel}
            onChange={(e) => setForm({ ...form, hostel: e.target.value })}
          >
            <option value="boys">Boys Hostel</option>
            <option value="girls">Girls Hostel</option>
          </select>
          <input
            className="border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-neutral-900 outline-none"
            placeholder="Room"
            value={form.room}
            onChange={(e) => setForm({ ...form, room: e.target.value })}
          />
          <input
            className="border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-neutral-900 outline-none"
            placeholder="Phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <input
            className="border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-neutral-900 outline-none"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 transition"
        >
          Add Student
        </button>
      </form>

      {/* LIST */}
      {error && <p className="text-sm text-neutral-500">{error}</p>}
      {loading ? (
        <p className="text-sm text-neutral-400">Loading...</p>
      ) : students.length === 0 ? (
        <p className="text-sm text-neutral-400">No students found.</p>
      ) : (
        <div className="overflow-auto border border-neutral-200 rounded-xl">
          <table className="w-full text-xs md:text-sm">
            <thead className="bg-neutral-50">
              <tr>
                <th className="p-2 text-left font-medium text-neutral-600">USN</th>
                <th className="p-2 text-left font-medium text-neutral-600">Name</th>
                <th className="p-2 text-left font-medium text-neutral-600">Hostel</th>
                <th className="p-2 text-left font-medium text-neutral-600">Room</th>
                <th className="p-2 text-left font-medium text-neutral-600">Phone</th>
                <th className="p-2 text-left font-medium text-neutral-600">Email</th>
                <th className="p-2 text-center font-medium text-neutral-600">QR Code</th>
                <th className="p-2 text-center font-medium text-neutral-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s) => {
                const qrSrc = isDemo ? qrCodes[s.usn] : s.qr_code;

                return (
                  <tr
                    key={s.id}
                    className="border-t border-neutral-100"
                  >
                    <td className="p-2 text-neutral-900">{s.usn}</td>
                    <td className="p-2 text-neutral-900">{s.name}</td>
                    <td className="p-2 capitalize text-neutral-700">{s.hostel}</td>
                    <td className="p-2 text-neutral-700">{s.room}</td>
                    <td className="p-2 text-neutral-700">{s.phone}</td>
                    <td className="p-2 text-neutral-700">{s.email}</td>

                    {/* QR COLUMN */}
                    <td className="p-2 text-center">
                      {qrSrc ? (
                        <div className="flex flex-col items-center space-y-1">
                          <img
                            src={qrSrc}
                            alt={`QR ${s.usn}`}
                            className="w-14 h-14 border border-neutral-200 rounded"
                          />

                          {!isDemo && (
                            <>
                              <a
                                href={`/bagtag?usn=${s.usn}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[10px] px-2 py-1 rounded bg-neutral-900 text-white font-medium"
                              >
                                Print Tag
                              </a>
                              <a
                                download={`QR-${s.usn}.png`}
                                href={qrSrc}
                                className="text-[10px] px-2 py-1 rounded bg-neutral-600 text-white font-medium"
                              >
                                Download
                              </a>
                            </>
                          )}
                        </div>
                      ) : (
                        <span className="text-[11px] text-neutral-400">
                          No QR
                        </span>
                      )}
                    </td>

                    {/* ACTIONS */}
                    <td className="p-2 text-center">
                      <button
                        onClick={() => handleDelete(s.usn)}
                        className="px-2 py-1 text-[11px] rounded bg-neutral-900 text-white font-medium hover:bg-neutral-800 transition"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/* ============================================================
   DB EDITOR — real admin only, not available in demo
   ============================================================ */
function DBEditor() {
  const [tables, setTables] = useState([]);
  const [selected, setSelected] = useState("");
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API_BASE}/api/editor/tables`)
      .then((res) => res.json())
      .then((data) => setTables(data))
      .catch(() => setError("Failed to load tables"));
  }, []);

  const loadTable = (table) => {
    if (!table || table === "select") return;
    setSelected(table);
    setLoading(true);
    setError("");

    fetch(`${API_BASE}/api/editor/table/${table}`)
      .then((res) => res.json())
      .then((data) => {
        setRows(data);
        setColumns(data.length > 0 ? Object.keys(data[0]) : []);
      })
      .catch(() => setError("Failed to load table data"))
      .finally(() => setLoading(false));
  };

  const updateCell = (r, col, value) => {
    const updated = [...rows];
    updated[r][col] = value;
    setRows(updated);
  };

  const saveRow = (index) => {
    if (!selected || columns.length === 0) return;
    const row = rows[index];
    const primaryKey = columns[0];

    fetch(`${API_BASE}/api/editor/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        table: selected,
        primaryKey,
        primaryKeyValue: row[primaryKey],
        updates: row,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        alert("Row updated");
      })
      .catch(() => alert("Failed to update row"));
  };

  const deleteRow = (index) => {
    if (!selected || columns.length === 0) return;
    const row = rows[index];
    const primaryKey = columns[0];

    if (!window.confirm("Delete this row?")) return;

    fetch(`${API_BASE}/api/editor/delete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        table: selected,
        primaryKey,
        id: row[primaryKey],
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        setRows(rows.filter((_, i) => i !== index));
      })
      .catch(() => alert("Failed to delete row"));
  };

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-5">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-base font-semibold text-neutral-900">Database Editor</h2>

        <select
          className="border border-neutral-200 px-3 py-1.5 rounded-lg text-sm bg-white focus:ring-2 focus:ring-neutral-900 outline-none"
          value={selected || "select"}
          onChange={(e) => loadTable(e.target.value)}
        >
          <option value="select">Select a table</option>
          {tables.map((t, idx) => {
            const name = Object.values(t)[0];
            return (
              <option key={idx} value={name}>
                {name}
              </option>
            );
          })}
        </select>
      </div>

      {error && <p className="text-sm text-neutral-500 mb-2">{error}</p>}
      {loading && <p className="text-sm text-neutral-400 mb-2">Loading...</p>}

      {!loading && selected && rows.length === 0 && (
        <p className="text-sm text-neutral-400">No rows in this table.</p>
      )}

      {!loading && rows.length > 0 && (
        <div className="overflow-auto border border-neutral-200 rounded-xl">
          <table className="w-full text-xs md:text-sm">
            <thead>
              <tr className="bg-neutral-50">
                {columns.map((col) => (
                  <th key={col} className="p-2 border-b border-neutral-200 text-left font-medium text-neutral-600">
                    {col}
                  </th>
                ))}
                <th className="p-2 border-b border-neutral-200 text-center font-medium text-neutral-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rIndex) => (
                <tr key={rIndex} className="border-t border-neutral-100">
                  {columns.map((col) => (
                    <td key={col} className="p-2">
                      <input
                        className="w-full bg-transparent outline-none border border-transparent focus:border-neutral-300 px-1 rounded text-neutral-900"
                        value={row[col] ?? ""}
                        onChange={(e) =>
                          updateCell(rIndex, col, e.target.value)
                        }
                      />
                    </td>
                  ))}
                  <td className="p-2 text-center space-x-1">
                    <button
                      onClick={() => saveRow(rIndex)}
                      className="px-2 py-1 bg-neutral-900 text-white text-[11px] rounded font-medium hover:bg-neutral-800 transition"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => deleteRow(rIndex)}
                      className="px-2 py-1 bg-neutral-500 text-white text-[11px] rounded font-medium hover:bg-neutral-600 transition"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   COMMON
   ============================================================ */
function StatusPill({ status }) {
  return (
    <span
      className="px-3 py-1 text-xs font-medium rounded-full bg-neutral-100 text-neutral-700 border border-neutral-200"
    >
      {status}
    </span>
  );
}
